import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Seo from 'components/Seo';
import { CHANGELOG, CHECKSUMS_URL, Glow, InkFooter, InkNav, LOGO_256, RELEASE, RELEASES, useInkRoot } from './shared';

/* Get Neevii Ink (/get-ink): licence cards, then downloads.
   Ported from code/public/_design/pricing-v2.html (Oct 2026).
   Prices are deliberately hidden for launch (Lee, Oct 2026): paid tiers read
   "Pricing to be announced" and nothing upsells a paid licence on download.
   Agreed prices for later: Freelancer GBP 79 one-time, Studio GBP 149 / seat / year
   (see neevii-website-build skill, references/ink-landing-decisions.md). */

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function mailtoFor(d) {
  const subject = `Neevii Ink licence enquiry: ${d.company || d.name}`;
  const body = `Name: ${d.name}\nEmail: ${d.email}\nCompany: ${d.company}\nSeats: ${d.seats}\n\n${d.message}`;
  return `mailto:ink-support@neevii.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

function useEscape(active, onClose) {
  useEffect(() => {
    if (!active) return undefined;
    const esc = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [active, onClose]);
}

function ContactModal({ open, onClose }) {
  const empty = { name: '', email: '', company: '', seats: '', message: '', website: '' };
  const [form, setForm] = useState(empty);
  const [status, setStatus] = useState({ kind: '', text: '' });
  const [sending, setSending] = useState(false);
  const firstField = useRef(null);
  useEscape(open, onClose);
  useEffect(() => {
    if (open) setTimeout(() => firstField.current && firstField.current.focus(), 30);
  }, [open]);
  if (!open) return null;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !EMAIL_RE.test(form.email) || !form.message.trim()) {
      setStatus({ kind: 'err', text: 'Please add your name, a valid email and a message.' });
      return;
    }
    setSending(true);
    setStatus({ kind: '', text: 'Sending…' });
    try {
      const r = await fetch('/api/contact/studio', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!r.ok) throw new Error(String(r.status));
      setStatus({ kind: 'ok', text: "Thanks, we'll be in touch soon." });
      setForm(empty);
    } catch (err) {
      setStatus({ kind: 'err', text: 'mailto', data: form });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="modal" role="dialog" aria-modal="true" aria-labelledby="ctitle" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <form className="cform" noValidate onSubmit={submit}>
        <button type="button" className="close" aria-label="Close" onClick={onClose}>×</button>
        <h3 id="ctitle">Licence enquiry</h3>
        <p className="sub">Tell us how you&apos;d use Neevii Ink and we&apos;ll reply from ink-support@neevii.com.</p>
        <div className="row">
          <label>Name<input ref={firstField} name="name" value={form.name} onChange={set('name')} autoComplete="name" required /></label>
          <label>Email<input name="email" type="email" value={form.email} onChange={set('email')} autoComplete="email" required /></label>
        </div>
        <div className="row">
          <label>Studio / company<input name="company" value={form.company} onChange={set('company')} autoComplete="organization" /></label>
          <label>Seats<input name="seats" inputMode="numeric" placeholder="e.g. 10" value={form.seats} onChange={set('seats')} /></label>
        </div>
        <label>
          Message
          <textarea name="message" placeholder="Pipeline, OS, air-gapped requirements…" value={form.message} onChange={set('message')} required />
        </label>
        <input className="hp" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" value={form.website} onChange={set('website')} />
        <div className="actions">
          <button className="btn btn-p" type="submit" disabled={sending}>Send enquiry</button>
          <span className={`msg ${status.kind}`} aria-live="polite">
            {status.text === 'mailto' ? (
              <>Could not send right now. <a href={mailtoFor(status.data)}>Email ink-support@neevii.com instead</a>.</>
            ) : (
              status.text
            )}
          </span>
        </div>
      </form>
    </div>
  );
}

function ThanksModal({ os, onClose }) {
  useEscape(Boolean(os), onClose);
  if (!os) return null;
  return (
    <div className="modal" role="dialog" aria-modal="true" aria-labelledby="tktitle" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="tk">
        <button type="button" className="close" aria-label="Close" onClick={onClose}>×</button>
        <img src={LOGO_256} alt="" width="64" height="64" />
        <h3 id="tktitle">Your download is starting</h3>
        <p>Neevii Ink for {os}. Free for personal use, enjoy.</p>
        <p>Local AI needs a GPU with about 8 GB of VRAM. Models download separately from inside the app.</p>
        <button type="button" className="btn btn-g" onClick={onClose}>Done</button>
      </div>
    </div>
  );
}

function ReleaseNotes({ entry }) {
  return (
    <>
      {entry.added && (
        <>
          <h4>New</h4>
          <ul>{entry.added.map((t) => <li key={t}>{t}</li>)}</ul>
        </>
      )}
      {entry.fixes && (
        <>
          <h4>Fixes</h4>
          <ul>{entry.fixes.map((t) => <li key={t}>{t}</li>)}</ul>
        </>
      )}
    </>
  );
}

function WhatsNew() {
  const [latest, ...older] = CHANGELOG;
  return (
    <section className="cl" id="whats-new" aria-labelledby="cltitle">
      <div className="mono eyebrow">What&apos;s new</div>
      <h2 id="cltitle">Builds &amp; updates</h2>
      <div className="cl-latest">
        <div className="cl-head">
          <span className="cl-badge">Latest</span>
          <b>{latest.version}</b>
          <span>build {latest.build} · {latest.date}</span>
        </div>
        <ReleaseNotes entry={latest} />
      </div>
      <details className="cl-old cl-archive">
        <summary>
          <b>Previous releases</b>
          <span>{older.length} builds</span>
        </summary>
        {older.map((entry) => (
          <details className="cl-old" key={entry.build}>
            <summary>
              <b>{entry.version}</b>
              <span>build {entry.build} · {entry.date}</span>
            </summary>
            <ReleaseNotes entry={entry} />
          </details>
        ))}
      </details>
    </section>
  );
}

export default function InkGetPage() {
  useInkRoot();
  const { hash, key: locationKey } = useLocation();
  const [contactOpen, setContactOpen] = useState(false);
  const [thanksOs, setThanksOs] = useState('');

  useEffect(() => {
    if (hash === '#contact') {
      setContactOpen(true);
      return;
    }
    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60);
    } else {
      window.scrollTo(0, 0);
    }
  }, [hash, locationKey]);

  const openContact = (e) => {
    e.preventDefault();
    setContactOpen(true);
  };
  const toDownload = (e) => {
    e.preventDefault();
    document.getElementById('download').scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="ink2">
      <Seo
        title="Get Neevii Ink: free for personal use, Linux & Windows"
        description="Download Neevii Ink, the scene-linear image editor built for VFX. Free for personal use, with offline installers for Linux and Windows."
        og={{ ogType: 'website' }}
      />
      <Glow />
      <InkNav />

      <main className="pg">
        <div className="wrap">
          <div className="mono eyebrow">Get Neevii Ink</div>
          <h1>
            Free for personal use.
            <br />
            <span className="inkword">Built for VFX.</span>
          </h1>
          <p className="lead">
            Learn, experiment and make your own work for free. Commercial licensing for freelancers and studios is
            coming soon, with pricing to be announced.
          </p>

          <div className="tiers">
            <div className="tier hl">
              <small>Personal</small>
              <div className="pr">Free</div>
              <ul>
                <li>Full editor, no watermark</li>
                <li>Local AI models</li>
                <li>Optional cloud providers (your own key)</li>
                <li>Windows &amp; Linux</li>
              </ul>
              <a className="btn btn-p" href="#download" onClick={toDownload}>↓ Download free</a>
            </div>
            <div className="tier">
              <small>Freelancer</small>
              <div className="pr tbd">Pricing to be announced</div>
              <ul>
                <li>Everything in Personal</li>
                <li>Full commercial use</li>
                <li>Pay once, no subscription</li>
                <li>Early access to new features</li>
                <li>Vote on the roadmap</li>
              </ul>
              <a className="btn btn-g" href="#contact" onClick={openContact}>Register interest</a>
            </div>
            <div className="tier">
              <small>Commercial · studio</small>
              <div className="pr tbd">Pricing to be announced</div>
              <ul>
                <li>For teams and companies</li>
                <li>Everything in Freelancer</li>
                <li>Volume discounts</li>
                <li>Air-gapped licence server</li>
                <li>Priority support for pipeline integration</li>
              </ul>
              <a className="btn btn-g" href="#contact" onClick={openContact}>Contact us</a>
            </div>
          </div>

          <div className="terms">
            <div>
              <h3>Free to use for</h3>
              <ul>
                <li>Learning and experimenting</li>
                <li>Hobby and personal projects</li>
                <li>Work that is not paid and not for any business or organisation</li>
              </ul>
            </div>
            <div>
              <h3>Commercial licences (coming soon) will cover</h3>
              <ul>
                <li>Work you are paid for</li>
                <li>Work for a client, freelance or employed</li>
                <li>Use inside a company, studio or agency</li>
              </ul>
            </div>
          </div>
          <p className="note">
            Local AI models run entirely on your machine. Cloud is optional: if you choose a cloud provider, you use
            your own API key and the provider&apos;s own terms and charges apply. You keep ownership of everything you
            create. Licence terms and pricing will be published before commercial licences go on sale.
          </p>

          <section className="dl" id="download">
            <div className="mono eyebrow or">Download</div>
            <h2>Download Neevii Ink.</h2>
            <p className="lead">
              Free for personal use · Latest: {RELEASE.version} (build {RELEASE.build}, {RELEASE.date}) · fully offline
              installers, no account needed. <a href="#whats-new">What&apos;s new in this build →</a>
            </p>
            <div className="oss">
              {RELEASES.map((rel) => (
                <div className="os-c" key={rel.key}>
                  <small>{rel.tag}</small>
                  <b>{rel.platform}</b>
                  <span>
                    {rel.arch} · {rel.format} · {rel.size}
                  </span>
                  <span className="hint">{rel.hint}</span>
                  <a className="btn btn-p" href={rel.url} onClick={() => setThanksOs(rel.platform)}>
                    ↓ Download for {rel.platform}
                  </a>
                </div>
              ))}
            </div>
            <div className="dlnotes">
              <span>
                <a href={CHECKSUMS_URL} target="_blank" rel="noreferrer">SHA-256 checksums</a> for both installers
              </span>
              <span>Local AI: about 8 GB VRAM recommended</span>
              <span>Models download separately</span>
            </div>
            <div className="nudge">
              <span>
                <b>Studio or commercial use?</b> Licensing is coming soon. Tell us about your team and we&apos;ll keep you
                posted.
              </span>
              <button type="button" onClick={() => setContactOpen(true)}>Get in touch →</button>
            </div>
          </section>

          <section className="dl" id="support" aria-labelledby="ink-support-title">
            <div className="mono eyebrow">Contact</div>
            <h2 id="ink-support-title">Ink support</h2>
            <p className="lead">
              Need help with Neevii Ink? Email <a href="mailto:ink-support@neevii.com">ink-support@neevii.com</a>.
            </p>
            <div className="ink-support-links">
              <Link className="btn btn-p" to="/ink/report-bug">Report a bug</Link>
              <Link className="btn btn-g" to="/ink/suggest-feature">Suggest a feature</Link>
            </div>
          </section>

          <WhatsNew />
        </div>
      </main>

      <InkFooter />
      <ThanksModal os={thanksOs} onClose={() => setThanksOs('')} />
      <ContactModal open={contactOpen} onClose={() => setContactOpen(false)} />
    </div>
  );
}
